import React, { useEffect, useState, useCallback } from "react";
import Header from "../../common/Header";
import Sidebar from "../../common/Sidebar";
import Footer from "../../common/Footer";
import PageHeader from "../../pages/PageHeader";
import axios from "axios";
import { triggerAlert } from "../../utils";
import Loader from "../../common/Loader";
import DataTable from "react-data-table-component";
import { Modal, ModalBody } from "react-bootstrap";
import { useForm } from "react-hook-form";
import Cookies from "js-cookie";

/**
 * Settings > Phone Number Pricing
 *
 * Dedicated admin grid for phone-number rentals (category='phone_number' on
 * AgentPlan). Phone numbers have no parent entity to configure, so they live
 * outside the per-entity edit pattern used for calling agents / chatbots /
 * workflows. Each row here is a number type (US local, US toll-free, etc.);
 * customers rent individual DIDs against these plans.
 */

export default function PhoneNumberPricing() {
  document.title = "Digital Employees - Phone Number Pricing";
  const mainHeading = "Phone Number Pricing";
  const heading = "Settings / Phone Number Pricing";

  const [isLoading, setIsLoading] = useState(true);
  const [listData, setListData] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const authHeaders = () => ({
    headers: {
      Authorization: `Bearer ${Cookies.get("admin_user_token")}`,
      "Content-Type": "application/json",
    },
  });

  const fetchListData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `/api/admin/agent-plans/?category=phone_number`,
        authHeaders()
      );
      const rows = Array.isArray(response.data)
        ? response.data
        : response.data?.results || [];
      rows.forEach((item, i) => {
        item.Num = i + 1;
      });
      setListData(rows);
    } catch (error) {
      setListData([]);
      triggerAlert("error", "Oops...", "Failed to load phone number pricing.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchListData();
  }, [fetchListData]);

  const openCreate = () => {
    setEditId(null);
    reset({
      agent_name: "",
      agent_key: "",
      pack_price: "",
      is_active: true,
    });
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditId(row.id);
    reset({
      agent_name: row.agent_name || "",
      agent_key: row.agent_key || "",
      pack_price: row.pack_price ?? "",
      is_active: !!row.is_active,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    if (submitting) return;
    setShowModal(false);
    reset();
  };

  const onSubmit = async (data) => {
    const payload = {
      agent_name: (data.agent_name || "").trim(),
      pack_price: parseFloat(data.pack_price),
      is_active: !!data.is_active,
    };
    if (!editId) {
      payload.agent_key = (data.agent_key || "").trim();
      payload.category = "phone_number";
      payload.pack_units = 1;
      payload.unit_label = "number";
    }

    setSubmitting(true);
    try {
      if (editId) {
        await axios.patch(
          `/api/admin/agent-plans/${editId}/`,
          payload,
          authHeaders()
        );
        triggerAlert("success", "Success", "Phone number pricing updated.");
      } else {
        await axios.post("/api/admin/agent-plans/", payload, authHeaders());
        triggerAlert("success", "Success", "Phone number plan created.");
      }
      setShowModal(false);
      reset();
      fetchListData();
    } catch (error) {
      const msg =
        error?.response?.data?.detail ||
        (error?.response?.data && typeof error.response.data === "object"
          ? Object.values(error.response.data).flat().join(" ")
          : null) ||
        "Something went wrong while saving.";
      triggerAlert("error", "Oops...", msg);
    } finally {
      setSubmitting(false);
    }
  };

  const toggleStatus = async (row) => {
    try {
      await axios.patch(
        `/api/admin/agent-plans/${row.id}/`,
        { is_active: !row.is_active },
        authHeaders()
      );
      triggerAlert("success", "Success", "Status updated.");
      fetchListData();
    } catch (error) {
      triggerAlert("error", "Oops...", "Could not update status.");
    }
  };

  const columns = [
    {
      name: "S.No",
      width: "70px",
      selector: (row) => row.Num,
      cell: (row) => <span>{row.Num}</span>,
    },
    {
      name: "Display Name",
      sortable: true,
      selector: (row) => row.agent_name,
      cell: (row) => <span>{row.agent_name}</span>,
    },
    {
      name: "Plan Key",
      sortable: true,
      selector: (row) => row.agent_key,
      cell: (row) => (
        <span style={{ fontFamily: "monospace", fontSize: "13px" }}>
          {row.agent_key}
        </span>
      ),
    },
    {
      name: "Monthly Price",
      sortable: true,
      selector: (row) => Number(row.pack_price),
      cell: (row) => (
        <span style={{ fontWeight: 600 }}>
          ${Number(row.pack_price || 0).toFixed(2)} /mo
        </span>
      ),
    },
    {
      name: "Status",
      sortable: true,
      selector: (row) => row.is_active,
      cell: (row) =>
        row.is_active ? (
          <span className="btn btn-success btn-sm activebtn btn-rounded waves-effect waves-light">
            Active
          </span>
        ) : (
          <span className="btn btn-danger btn-sm Inactivebtn btn-rounded waves-effect waves-light">
            Inactive
          </span>
        ),
    },
    {
      name: "Action",
      cell: (row) => (
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => openEdit(row)}
          >
            Edit
          </button>
          <button
            type="button"
            className={`btn btn-sm ${
              row.is_active ? "btn-outline-danger" : "btn-outline-success"
            }`}
            onClick={() => toggleStatus(row)}
          >
            {row.is_active ? "Deactivate" : "Activate"}
          </button>
        </div>
      ),
    },
  ];

  const customStyles = {
    table: { style: { backgroundColor: "#ffffff", borderRadius: "8px" } },
    headRow: {
      style: {
        backgroundColor: "#064c77",
        borderBottom: "1px solid #dee2e6",
        minHeight: "52px",
      },
    },
    headCells: {
      style: {
        color: "#ffffff",
        fontSize: "13px",
        fontWeight: "700",
        textTransform: "uppercase",
      },
    },
    rows: {
      style: {
        minHeight: "56px",
        backgroundColor: "#ffffff",
        "&:hover": { backgroundColor: "#f8f9fa", cursor: "pointer" },
      },
    },
    cells: {
      style: {
        fontSize: "14px",
        color: "#495057",
        borderBottom: "1px solid #f1f3f5",
      },
    },
    pagination: { style: { borderTop: "1px solid #dee2e6", padding: "8px" } },
    noData: {
      style: {
        backgroundColor: "#ffffff",
        color: "#6c757d",
        padding: "24px",
      },
    },
  };

  return (
    <>
      <div id="layout-wrapper">
        <Header />
        <Sidebar />

        <div className="main-content">
          <div className="page-content" style={{ backgroundColor: "#ededed" }}>
            <div className="container-fluid">
              <PageHeader mainHeading={mainHeading} heading={heading} />

              <div className="row">
                <div className="col-12">
                  <div className="card">
                    <div className="card-body">
                      <div className="alert alert-info py-2 mb-3" role="alert">
                        Each row is a phone-number <strong>type</strong> (e.g.,
                        US Local, US Toll-Free). The price is the{" "}
                        <strong>monthly recurring rental</strong> a customer
                        pays per number they rent under this plan.
                      </div>

                      <div className="row align-items-center mb-3">
                        <div className="col-md-8"></div>
                        <div className="col-md-4 text-end">
                          <button
                            type="button"
                            className="btn btn-primary w-md btn-md"
                            onClick={openCreate}
                          >
                            Create Plan
                          </button>
                        </div>
                      </div>

                      {!isLoading ? (
                        <DataTable
                          columns={columns}
                          data={listData}
                          pagination
                          fixedHeader
                          fixedHeaderScrollHeight="600px"
                          persistTableHead
                          customStyles={customStyles}
                        />
                      ) : (
                        <div className="loader-overlay text-white">
                          <Loader />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </div>

      <div className="rightbar-overlay"></div>

      <Modal
        size="md"
        scrollable
        show={showModal}
        onHide={closeModal}
        className="modal fade"
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header
          className="modal-header_box bg-my-primary p-3 d-flex justify-content-between"
          closeButton
        >
          <h1 className="modal-title fs-5">
            {editId ? "Edit" : "Create"} Phone Number Plan
          </h1>
        </Modal.Header>

        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalBody>
            <div className="row">
              <div className="col-12 mb-3">
                <label className="form-label">
                  Display Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder='e.g. "US Local"'
                  {...register("agent_name", {
                    required: "Display name is required",
                  })}
                />
                {errors.agent_name && (
                  <div className="text-danger font_14 mt-1">
                    {errors.agent_name.message}
                  </div>
                )}
              </div>

              <div className="col-12 mb-3">
                <label className="form-label">
                  Plan Key <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  disabled={!!editId}
                  placeholder="e.g. phone_local_us"
                  {...register("agent_key", {
                    required: "Plan key is required",
                  })}
                />
                {errors.agent_key && (
                  <div className="text-danger font_14 mt-1">
                    {errors.agent_key.message}
                  </div>
                )}
                <small className="text-muted">
                  Unique identifier — can't be changed later.
                </small>
              </div>

              <div className="col-12 mb-3">
                <label className="form-label">
                  Monthly Price ($) <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="form-control"
                  placeholder="e.g. 5.00"
                  {...register("pack_price", {
                    required: "Price is required",
                    min: { value: 0, message: "Price can't be negative" },
                  })}
                />
                {errors.pack_price && (
                  <div className="text-danger font_14 mt-1">
                    {errors.pack_price.message}
                  </div>
                )}
              </div>

              <div className="col-12">
                <div className="form-check mb-2">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="pnp_is_active"
                    {...register("is_active")}
                  />
                  <label className="form-check-label" htmlFor="pnp_is_active">
                    Is Active
                  </label>
                </div>
              </div>
            </div>
          </ModalBody>
          <Modal.Footer>
            <div className="py-2">
              <button
                type="button"
                className="btn btn-warning w-md btn-md me-2"
                onClick={closeModal}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary w-md btn-md"
                disabled={submitting}
              >
                {submitting ? "Saving..." : "Save"}
              </button>
            </div>
          </Modal.Footer>
        </form>
      </Modal>
    </>
  );
}
